import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useCardStyles } from './styles';
import { Collapse } from '@tcl-boron-prefabs/collapse';
import { mergeClasses } from '@tcl-argon-utils/utils';

/**
 * The Card component must be passed a single child.
 * The child will take up the entire area of the Card
 */

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface CardProps extends Omit<DivElement, 'onChange'> {
  /** card content */
  children: JSX.Element;
  /** optional card title */
  title?: string;
  /** symantic html heading level for title*/
  headerLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** should the card be able to expand and collapse via a clickable chevron */
  isCollapsible?: boolean;
  /** A handler triggered whenever this toggle is toggled by mouse or keyboard input. */
  onChange?: (value: boolean) => void;
  /** If `true`, apply show the JSX Element. If `false`, then hide the JSX Element. */
  isOpen?: boolean;
  /** Style classNames to be applied in addition to the base classNames */
  classes?: Record<string, string>;
}

export const Card: React.FC<CardProps> = ({
  children,
  headerLevel = 'h2',
  title = '',
  isCollapsible = false,
  onChange = () => {},
  isOpen = false,
  className,
  classes: classOverrides,
  ...props
}) => {
  const classes = mergeClasses(useCardStyles({ isCollapsible }), classOverrides, { root: className });
  const Header = headerLevel;

  return (
    <div aria-label="card" className={classes.root} data-analytics="card" role="region" {...props}>
      {isCollapsible && (
        <Collapse title={title} isOpen={isOpen} onChange={onChange}>
          {children}
        </Collapse>
      )}
      {title !== '' && !isCollapsible && (
        <div className={classes.cardContentRoot}>
          <div className={classes.cardContentWrapper}>
            <Header className={classes.title}>{title}</Header>
            <div> {children}</div>
          </div>
        </div>
      )}
      {title === '' && !isCollapsible && (
        <div className={classes.cardContentRoot}>
          <div className={classes.cardContentWrapper}>{children}</div>
        </div>
      )}
    </div>
  );
};
